import type { ComunidadAudienceMode } from "@/hooks/api/comunidad";

/** Categorías (ranking_category) que se pueden elegir. Mismo listado que el backend
 *  (`COMUNIDAD_AUDIENCE_CATEGORIES` en comunidad-content.schema.ts). */
export const AUDIENCE_CATEGORIES = ["N", "NZ", "O", "F"] as const;

export interface NewsAudience {
  audienceMode: ComunidadAudienceMode;
  audienceCategories: string[];
}

export const DEFAULT_AUDIENCE: NewsAudience = { audienceMode: "all", audienceCategories: [] };

const MODES: { value: ComunidadAudienceMode; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "exclude", label: "Todas excepto…" },
  { value: "include", label: "Solo…" },
];

/** "Solo… / Todas excepto…" sin ninguna categoría tildada no es válido. */
export const isAudienceValid = (a: NewsAudience) => a.audienceMode === "all" || a.audienceCategories.length > 0;

/** Texto corto para la vista de la novedad en el panel ("Solo N, NZ"). null = todas. */
export function describeAudience(a: NewsAudience): string | null {
  if (a.audienceMode === "all") return null;
  const list = a.audienceCategories.join(", ");
  return a.audienceMode === "include" ? `Solo ${list}` : `Todas excepto ${list}`;
}

interface NewsAudiencePickerProps {
  value: NewsAudience;
  onChange: (value: NewsAudience) => void;
}

/** "¿Quién puede ver esto?" — en el alta y la edición de cada novedad del panel de admin. */
export const NewsAudiencePicker = ({ value, onChange }: NewsAudiencePickerProps) => {
  const toggleCategory = (cat: string) => {
    const has = value.audienceCategories.includes(cat);
    onChange({
      ...value,
      audienceCategories: has
        ? value.audienceCategories.filter((c) => c !== cat)
        : [...value.audienceCategories, cat],
    });
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[#E3D9FF] bg-[#FAF8FF] px-3 py-2.5">
      <span className="text-xs font-bold text-[#4A3D73]">¿Quién puede ver esto?</span>
      <div className="flex flex-wrap gap-2">
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() =>
              onChange({
                audienceMode: m.value,
                audienceCategories: m.value === "all" ? [] : value.audienceCategories,
              })
            }
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              value.audienceMode === m.value
                ? "border-[#665090] bg-[#665090] text-white"
                : "border-[#C9BCE8] bg-white text-[#665090]"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      {value.audienceMode !== "all" && (
        <div className="flex flex-wrap items-center gap-3">
          {AUDIENCE_CATEGORIES.map((cat) => (
            <label key={cat} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-[#2D2049]">
              <input
                type="checkbox"
                className="accent-[#665090]"
                checked={value.audienceCategories.includes(cat)}
                onChange={() => toggleCategory(cat)}
              />
              {cat}
            </label>
          ))}
          {!isAudienceValid(value) && <span className="text-[11px] text-amber-700">Tildá al menos una categoría</span>}
        </div>
      )}
    </div>
  );
};

export default NewsAudiencePicker;
